/**
 * Shared "poll until done or timeout" primitive. Each createPoller() call
 * returns an independent closure — no state is shared between instances —
 * so stopping one poller can never affect another.
 */

export interface CreatePollerOptions<T> {
  fetchStatus: () => Promise<T>;
  isDone: (data: T) => boolean;
  onUpdate: (data: T) => void;
  onDone?: (data: T) => void;
  onTimeout?: (lastData: T | undefined) => void;
  onError?: (err: unknown) => void;
  intervalMs?: number;
  maxAttempts?: number;
  requestTimeoutMs?: number;
}

export interface Poller {
  start: () => void;
  stop: () => void;
  isActive: () => boolean;
}

const DEFAULT_INTERVAL_MS = 5000;
const DEFAULT_MAX_ATTEMPTS = 120; // 10 minutes at the default interval
const DEFAULT_REQUEST_TIMEOUT_MS = 30000;

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  let timer: ReturnType<typeof setTimeout>;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new Error(`Request timed out after ${ms}ms`)), ms);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}

export function createPoller<T>(opts: CreatePollerOptions<T>): Poller {
  const {
    fetchStatus,
    isDone,
    onUpdate,
    onDone,
    onTimeout,
    onError,
    intervalMs = DEFAULT_INTERVAL_MS,
    maxAttempts = DEFAULT_MAX_ATTEMPTS,
    requestTimeoutMs = DEFAULT_REQUEST_TIMEOUT_MS,
  } = opts;

  let timerId: ReturnType<typeof setTimeout> | null = null;
  let attempt = 0;
  let stopped = true;
  let lastData: T | undefined;

  const clearTimer = () => {
    if (timerId !== null) {
      clearTimeout(timerId);
      timerId = null;
    }
  };

  const stop = () => {
    stopped = true;
    clearTimer();
  };

  const tick = async () => {
    attempt += 1;
    if (attempt > maxAttempts) {
      stop();
      onTimeout?.(lastData);
      return;
    }
    try {
      const data = await withTimeout(fetchStatus(), requestTimeoutMs);
      if (stopped) return;
      lastData = data;
      onUpdate(data);
      if (isDone(data)) {
        stop();
        onDone?.(data);
        return;
      }
    } catch (err) {
      if (stopped) return;
      onError?.(err);
    }
    if (!stopped) {
      timerId = setTimeout(tick, intervalMs);
    }
  };

  return {
    start: () => {
      stop();
      attempt = 0;
      lastData = undefined;
      stopped = false;
      tick();
    },
    stop,
    isActive: () => !stopped,
  };
}

export function mergeRecord<V>(prev: Record<string, V>, next: Record<string, V>): Record<string, V> {
  return { ...prev, ...next };
}

export function buildTimedOutStatuses<TInfo extends { status: string; error?: string }>(
  ids: string[],
  lastData: { statuses: Record<string, TInfo> } | undefined,
  isNonTerminal: (status: TInfo["status"]) => boolean,
  timeoutInfo: TInfo,
): Record<string, TInfo> {
  const result: Record<string, TInfo> = {};
  ids.forEach((id) => {
    const info = lastData?.statuses[id];
    result[id] = info && !isNonTerminal(info.status) ? info : timeoutInfo;
  });
  return result;
}
